/**
 * The merged comment thread + system events — lifted out of
 * `ActionItemDetail.tsx` (ADR-0221 Phase 0, E5) so the issue page shows the
 * same Activity tab. Behaviour unchanged for action items.
 *
 * Single chronological stream rendered newest-first with the comment input
 * pinned to the top. System rows render as one line of text with an optional
 * note block under it; comments render with their full body, and the author
 * may edit their own inline. What a system row SAYS is the caller's
 * (`formatRow`): the action-item page composes it console-side, the issue
 * page shows the hub's served sentence.
 */

import { useMemo, useRef, useState } from "react";
import { formatTimestamp } from "../lib/formatTime.js";

export interface ThreadActivity {
  id: string;
  created_at: string;
}

export interface ThreadComment {
  id: string;
  body: string;
  created_at: string;
  edited_at: string | null;
}

export type TimelineEntry<A extends ThreadActivity, C extends ThreadComment> =
  | { kind: "activity"; id: string; at: string; row: A }
  | { kind: "comment"; id: string; at: string; row: C };

/**
 * Merge system rows and comments, newest first. A `comment` activity row is
 * dropped because the comment itself is in the stream — keeping it would be
 * one post shown twice.
 */
export function buildTimeline<A extends ThreadActivity & { event_type: string }, C extends ThreadComment>(
  activity: A[],
  comments: C[]
): TimelineEntry<A, C>[] {
  const events: TimelineEntry<A, C>[] = activity
    .filter((a) => a.event_type !== "comment")
    .map((a) => ({ kind: "activity" as const, id: `a-${a.id}`, at: a.created_at, row: a }));
  for (const c of comments) {
    events.push({ kind: "comment" as const, id: `c-${c.id}`, at: c.created_at, row: c });
  }
  events.sort((a, b) => b.at.localeCompare(a.at));
  return events;
}

export function ActivityThread<A extends ThreadActivity & { event_type: string }, C extends ThreadComment>(props: {
  comments: C[];
  activity: A[];
  /** One system row: who did it, what it says, and an optional note block under it. */
  formatRow: (row: A) => { actor: string; text: string; note: string | null };
  /** A comment's author: the id the edit right is checked against, and the name shown. */
  commentAuthor: (c: C) => { id: string; label: string };
  currentUserId: string | null;
  onAdd: (body: string) => Promise<void>;
  onEdit: (commentId: string, body: string) => Promise<void>;
  /** Shown when there is nothing yet. */
  emptyText?: string;
}) {
  const { comments, activity, currentUserId, onAdd, onEdit } = props;

  // Comment input state.
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Per-comment edit state.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);

  const stream = useMemo(() => buildTimeline(activity, comments), [activity, comments]);

  async function submitComment() {
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onAdd(trimmed);
      setBody("");
      taRef.current?.focus();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submitComment();
    }
  }

  function startEdit(c: C) {
    setEditingId(c.id);
    setEditBody(c.body);
    setEditErr(null);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditBody("");
    setEditErr(null);
  }
  async function saveEdit() {
    if (!editingId || editBusy) return;
    const trimmed = editBody.trim();
    if (!trimmed) {
      setEditErr("Comment cannot be empty.");
      return;
    }
    setEditBusy(true);
    setEditErr(null);
    try {
      await onEdit(editingId, trimmed);
      cancelEdit();
    } catch (e) {
      setEditErr(e instanceof Error ? e.message : String(e));
    } finally {
      setEditBusy(false);
    }
  }
  function onEditKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void saveEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  }

  return (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      {/* Comment input — pinned to top, compact */}
      <div style={{ display: "grid", gap: 6 }}>
        <textarea
          ref={taRef}
          className="input"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Add a comment — Enter to submit, Shift+Enter for newline"
          style={{ fontSize: 13 }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          {err
            ? <span className="help" style={{ color: "var(--danger-text)" }}>{err}</span>
            : <span />}
          <button
            type="button"
            className="btn btnSm btnPrimary"
            disabled={busy || !body.trim()}
            onClick={() => void submitComment()}
          >
            {busy ? "Adding…" : "Comment"}
          </button>
        </div>
      </div>

      {/* Merged stream — newest first. Two-column row: timestamp on the left, content on the right. */}
      {stream.length === 0 ? (
        <div className="muted">{props.emptyText ?? "No activity yet."}</div>
      ) : (
        <ul className="aiActivityList" style={{ margin: 0, paddingLeft: 0, listStyle: "none", borderTop: "1px solid var(--border)" }}>
          {stream.map((entry) => {
            if (entry.kind === "activity") {
              const a = entry.row;
              const { actor, text, note } = props.formatRow(a);
              return (
                <li key={entry.id} className="aiActivityRow">
                  <span className="aiActivityTime">{formatTimestamp(a.created_at)}</span>
                  <div className="aiActivityBody">
                    <div>
                      <span className="aiActivityActor">{actor}</span>
                      <span className="aiActivitySystemText">{text}</span>
                    </div>
                    {note && <div className="aiActivityCommentBody">{note}</div>}
                  </div>
                </li>
              );
            }
            const c = entry.row;
            const author = props.commentAuthor(c);
            const isAuthor = currentUserId === author.id;
            const isEditing = editingId === c.id;
            return (
              <li key={entry.id} className="aiActivityRow">
                <span className="aiActivityTime">
                  {formatTimestamp(c.created_at)}
                  {c.edited_at && <><br /><span style={{ fontStyle: "italic" }}>edited {formatTimestamp(c.edited_at)}</span></>}
                </span>
                <div className="aiActivityBody">
                  <span className="aiActivityActor">{author.label}</span>
                  <span className="aiActivityCommentMeta"> commented</span>
                  {isAuthor && !isEditing && (
                    <button
                      type="button"
                      className="adminListAction"
                      style={{ marginLeft: 8 }}
                      onClick={() => startEdit(c)}
                    >
                      Edit
                    </button>
                  )}
                  {isEditing ? (
                    <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
                      <textarea
                        className="input"
                        rows={3}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        onKeyDown={onEditKeyDown}
                        autoFocus
                      />
                      {editErr && <div className="help" style={{ color: "var(--danger-text)" }}>{editErr}</div>}
                      <div className="actions">
                        <button className="btn btnSm" onClick={cancelEdit} disabled={editBusy}>Cancel</button>
                        <button className="btn btnSm btnPrimary" onClick={() => void saveEdit()} disabled={editBusy}>
                          {editBusy ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="aiActivityCommentBody">{c.body}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
